import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, finalize, switchMap, map, throwError } from 'rxjs';

import { TargetAudience } from '../models/target-audience';
import { TargetAudienceLocation } from '../models/target-audience-location';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-audience-detail',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './audience-detail.component.html',
  styleUrl: './audience-detail.component.css'
})
export class AudienceDetailComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  private subscription: Subscription | null = null;

  protected readonly serviceId = signal<number | null>(null);
  protected readonly audienceId = signal<number | null>(null);
  protected readonly audience = signal<TargetAudience | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');

  protected readonly locations = signal<TargetAudienceLocation[]>([]);
  protected readonly targetAreaGenerated = signal<boolean>(false);
  protected readonly locationStatus = signal<string>('');
  protected readonly locationError = signal<string>('');
  protected readonly locationLoading = signal<boolean>(false);
  protected readonly hasTargetAreas = computed(
    () => this.targetAreaGenerated() || this.locations().length > 0
  );
  protected readonly targetAreaActionLabel = computed(() =>
    this.hasTargetAreas() ? 'View Target Area' : 'Generate Target Area'
  );

  constructor() {
    this.subscription = this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const audienceParam = params.get('audienceId');

      const parsedServiceId = serviceParam ? Number(serviceParam) : NaN;
      const parsedAudienceId = audienceParam ? Number(audienceParam) : NaN;

      if (Number.isNaN(parsedServiceId) || Number.isNaN(parsedAudienceId)) {
        this.errorMessage.set('Invalid identifiers for this audience.');
        this.loading.set(false);
        return;
      }

      this.serviceId.set(parsedServiceId);
      this.audienceId.set(parsedAudienceId);
      this.uiState.setServiceId(parsedServiceId);
      this.uiState.setAudienceId(parsedAudienceId);
      this.targetAreaGenerated.set(
        this.uiState.isTargetAreaGenerated(parsedServiceId, parsedAudienceId)
      );
      this.loadAudience(parsedServiceId, parsedAudienceId);
      this.loadLocations(parsedServiceId, parsedAudienceId);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  protected goBack(): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/audiences', serviceId]);
  }

  protected regenerateTargetAreas(generateAll = false): void {
    const serviceId = this.serviceId();
    const audienceId = this.audienceId();
    if (!serviceId) {
      this.locationError.set('Missing service identifier to regenerate target areas.');
      return;
    }
    if (!generateAll && !audienceId) {
      this.locationError.set(
        'Missing identifiers to regenerate target areas.'
      );
      return;
    }

    this.locationError.set('');
    this.locationStatus.set(
      generateAll
        ? 'Generating target areas for all audiences...'
        : 'Generating target areas...'
    );
    this.locationLoading.set(true);

    const generation$ = generateAll
      ? this.collectDataService.createTargetAudienceLocations(serviceId)
      : this.collectDataService.createTargetAudienceLocationsForAudience(
          serviceId,
          audienceId as number
        );

    generation$
      .pipe(
        switchMap((result) => {
          const isSuccess = result?.status?.toLowerCase() === 'success';
          if (!isSuccess) {
            const message =
              result?.message ||
              'Target area generation did not complete. Please try again.';
            return throwError(() => new Error(message));
          }

          return this.collectDataService.getLocationsByService(serviceId).pipe(
            map((locations) => ({
              result,
              locations: locations.filter((location) => {
                if (!audienceId) {
                  return false;
                }
                return Number(location.target_audience_id) === audienceId;
              })
            }))
          );
        }),
        finalize(() => {
          this.locationLoading.set(false);
        })
      )
      .subscribe({
        next: ({ result, locations }) => {
          this.locations.set(locations);
          const stored = result?.stored_rows ?? locations.length;
          this.locationStatus.set(
            result?.message ||
              `Target areas generated successfully. Stored ${stored} record(s).`
          );
          if (serviceId && audienceId) {
            this.targetAreaGenerated.set(true);
            this.uiState.markTargetAreaGenerated(serviceId, audienceId);
          } else {
            this.targetAreaGenerated.set(true);
          }
          this.viewTargetAreas();
        },
        error: (error) => {
          const message =
            error?.message ||
            'Unable to regenerate target areas. Please try again.';
          this.locationError.set(message);
          this.locationStatus.set('');
        }
      });
  }

  protected generateAllTargetAreas(): void {
    if (this.locationLoading()) {
      return;
    }

    this.regenerateTargetAreas(true);
  }

  protected handleTargetAreaAction(): void {
    if (this.locationLoading()) {
      return;
    }

    if (this.hasTargetAreas()) {
      this.viewTargetAreas();
      return;
    }

    this.regenerateTargetAreas();
  }

  protected viewTargetAreas(): void {
    const serviceId = this.serviceId();
    const audienceId = this.audienceId();
    if (!serviceId) {
      return;
    }

    const queryParams = audienceId ? { audienceId } : undefined;
    this.router.navigate(['/target-areas', serviceId], { queryParams });
  }

  protected audienceTitle = computed(() => {
    const current = this.audience();
    return current?.target_audience || 'Target Audience Details';
  });

  protected renderText(value: unknown): string {
    const tokens = this.renderArray(value);
    if (tokens.length) {
      return tokens.join(', ');
    }

    if (value && typeof value === 'object') {
      const summary = this.renderObjectSummary(value);
      return summary.length ? summary.join(' | ') : 'N/A';
    }

    return 'N/A';
  }

  protected renderArray(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item == null) {
            return '';
          }
          if (typeof item === 'string') {
            return item.trim();
          }
          if (typeof item === 'number' || typeof item === 'boolean') {
            return String(item);
          }
          return JSON.stringify(item);
        })
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return this.renderArray(parsed);
        }
        if (parsed && typeof parsed === 'object') {
          return [JSON.stringify(parsed)];
        }
      } catch {
        // ignore parsing failures and treat as plain text
      }

      return [trimmed];
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return [String(value)];
    }

    return [];
  }

  protected renderObjectSummary(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    let source = value;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          source = parsed;
        } else {
          return [trimmed];
        }
      } catch {
        return [trimmed];
      }
    }

    if (Array.isArray(source)) {
      return this.renderArray(source);
    }

    if (typeof source !== 'object') {
      return this.renderArray(source);
    }

    return Object.entries(source as Record<string, unknown>).map(
      ([key, val]) => {
        const values = this.renderArray(val);
        const content =
          values.length || (val && typeof val === 'object')
            ? values.length
              ? values.join(', ')
              : JSON.stringify(val)
            : 'N/A';
        return `${this.formatKey(key)}: ${content}`;
      }
    );
  }

  private loadAudience(serviceId: number, audienceId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.collectDataService
      .getTargetAudiences(serviceId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (audiences) => {
          const matched = audiences.find((item) => item.id === audienceId);
          if (!matched) {
            this.errorMessage.set(
              'Target audience was not found. It may have been removed.'
            );
            this.audience.set(null);
            return;
          }
          this.audience.set(matched);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load the selected audience.';
          this.errorMessage.set(message);
          this.audience.set(null);
        }
      });
  }

  private loadLocations(serviceId: number, audienceId: number): void {
    this.locationLoading.set(true);
    this.locationError.set('');
    this.locationStatus.set('Loading associated target areas...');

    this.collectDataService
      .getLocationsByService(serviceId)
      .pipe(
        finalize(() => {
          this.locationLoading.set(false);
        })
      )
      .subscribe({
        next: (locations) => {
          const filtered = locations.filter(
            (location) => Number(location.target_audience_id) === audienceId
          );
          this.locations.set(filtered);
          const hasLocations = filtered.length > 0;
          if (serviceId && audienceId) {
            if (hasLocations) {
              this.uiState.markTargetAreaGenerated(serviceId, audienceId);
            } else {
              this.uiState.unmarkTargetAreaGenerated(serviceId, audienceId);
            }
          }
          this.targetAreaGenerated.set(
            hasLocations ||
              (serviceId && audienceId
                ? this.uiState.isTargetAreaGenerated(serviceId, audienceId)
                : false)
          );
          this.locationStatus.set(
            filtered.length
              ? `Loaded ${filtered.length} target area(s) for this audience.`
              : 'No target areas are linked to this audience yet.'
          );
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load target areas for this audience.';
          this.locationError.set(message);
          this.locationStatus.set('');
          this.locations.set([]);
        }
      });
  }

  private formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
